<template>
  <v-dialog
    :model-value="modelValue"
    max-width="520"
    persistent
    content-class="daum-postcode-dialog-shell"
    @update:model-value="onDialogToggle"
  >
    <div class="daum-postcode-dialog">
      <header class="daum-postcode-dialog__header">
        <h3>주소 검색</h3>
        <button type="button" class="daum-postcode-dialog__close" aria-label="닫기" @click="close">
          <v-icon size="20">mdi-close</v-icon>
        </button>
      </header>
      <div class="daum-postcode-dialog__body">
        <div v-if="loading" class="daum-postcode-dialog__loading">
          <v-progress-circular indeterminate color="primary" size="40" />
          <span>주소 검색을 불러오는 중...</span>
        </div>
        <div v-show="!loading" ref="containerRef" class="daum-postcode-dialog__embed"></div>
      </div>
    </div>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, watch, nextTick } from 'vue';

export interface DaumPostcodeResult {
  zonecode: string;
  address: string;
  roadAddress: string;
  jibunAddress: string;
  buildingName: string;
}

const props = defineProps<{
  modelValue: boolean;
}>();

const emit = defineEmits<{
  'update:modelValue': [boolean];
  select: [DaumPostcodeResult];
}>();

const loading = ref(false);
const containerRef = ref<HTMLDivElement | null>(null);
let scriptLoading: Promise<void> | null = null;

const loadScript = (): Promise<void> => {
  if ((window as any).daum?.Postcode) return Promise.resolve();
  if (scriptLoading) return scriptLoading;
  scriptLoading = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Daum Postcode script load failed'));
    document.head.appendChild(script);
  });
  return scriptLoading;
};

const close = () => {
  emit('update:modelValue', false);
};

const onDialogToggle = (val: boolean) => {
  if (!val) close();
};

const embed = async () => {
  loading.value = true;
  try {
    await loadScript();
    await nextTick();
    const container = containerRef.value;
    if (!container) throw new Error('container not ready');
    container.innerHTML = '';
    const Postcode = (window as any).daum?.Postcode;
    if (!Postcode) throw new Error('Daum Postcode is undefined');
    new Postcode({
      oncomplete: (data: any) => {
        const road = data.roadAddress || '';
        const jibun = data.jibunAddress || '';
        emit('select', {
          zonecode: data.zonecode || '',
          address: road || jibun || '',
          roadAddress: road,
          jibunAddress: jibun,
          buildingName: data.buildingName || '',
        });
        close();
      },
      onresize: (size: any) => {
        if (container && size?.height) {
          container.style.height = `${size.height}px`;
        }
      },
      width: '100%',
      height: '100%',
    }).embed(container, { autoClose: true });
    loading.value = false;
  } catch (e) {
    loading.value = false;
    close();
    alert('주소 검색을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
  }
};

watch(
  () => props.modelValue,
  (val) => {
    if (val) embed();
  }
);
</script>
